import SvgDelivery from '@common/icon/SvgDelivery';
import SvgDonations from '@common/icon/SvgDonations';
import SvgDonationTax from '@common/icon/SvgDonationTax';
import SvgOrders from '@common/icon/SvgOrders';
import SvgPointStore from '@common/icon/SvgPointStore';
import SvgReview from '@common/icon/SvgReview';
import { useQueryClient } from '@tanstack/react-query';

import { AuthQueryOptions, TERM_CATEGORY } from '@/api';
import { GOV_CODE } from '@/app/donation/constants';
import { DONATION_ROUTES, ROUTES } from '@/constants';
import { useCheckKakaobank } from '@/hooks/useCheckKakaobank';
import { useCheckTossOrKbPay } from '@/hooks/useCheckTossOrKbPay';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useLoginGuard } from '@/hooks/useLoginGuard';
import { openResponsiveDialog } from '@/stores/ResponsiveDialogStore';

import { JejuCertificateContent } from '../(private)/my/(benefit)/jeju/_components/JejuCertificateContent';

interface NavigationItem {
  label: string;
  href: string;
  //* 아이콘이 있으면 아이콘 행, 없으면 chevron 행으로 렌더된다
  icon?: React.ReactElement;
  //* href가 '#'(onClick 분기)이거나 실제 도달 경로가 href보다 깊을 때의 활성 판정 기준
  activePathname?: string;
  isNew?: boolean;
  onClick?: () => void;
}

interface NavigationGroup {
  label: string;
  items: NavigationItem[];
}

export const useNavigationGroupList = () => {
  const router = useCustomRouter();
  const loginGuard = useLoginGuard();
  const queryClient = useQueryClient();
  const isKakaobank = useCheckKakaobank();
  const isTossOrKbPay = useCheckTossOrKbPay();

  const NAVIGATION_GROUP_LIST = {
    MY_MENU: {
      label: 'MY 메뉴',
      items: [
        {
          label: '기부내역',
          href: DONATION_ROUTES.MY_DONATIONS.pathname,
          icon: <SvgDonations className="size-24" originalFill />,
        },
        {
          label: '주문내역',
          href: ROUTES.MY_ORDERS.pathname,
          icon: <SvgOrders className="size-24" originalFill />,
        },
        {
          label: '배송지 관리',
          href: ROUTES.MY_DELIVERY_ADDRESS.pathname,
          icon: <SvgDelivery className="size-24" originalFill />,
        },
        {
          label: '내가 남긴 후기',
          href: ROUTES.MY_REVIEWS.pathname,
          icon: <SvgReview className="size-24" originalFill />,
        },
        {
          label: '탐나는 제주패스',
          href: '#',
          activePathname: '/donation/my/jeju',
          onClick: async () => {
            if (!loginGuard()) {
              return;
            }

            const myDonation = await queryClient
              .ensureQueryData(
                AuthQueryOptions['get:/wello/v2/auth/home-donation/my'](),
              )
              .catch(() => null);
            const isDonatedToJeju =
              myDonation?.context?.homeLoveGovCodeList?.includes(
                GOV_CODE.JEJU,
              ) ?? false;

            if (isDonatedToJeju) {
              return openResponsiveDialog({
                component: JejuCertificateContent,
                ariaLabel: '제주 기부 인증서',
                drawerType: 'bottom',
              });
            }

            router.push(DONATION_ROUTES.MY_JEJU_RECOMMEND_INFO.pathname);
          },
          icon: <SvgDonationTax className="size-24" originalFill />,
        },
        ...(isKakaobank
          ? []
          : [
              {
                label: '웰포인트상점',
                href: ROUTES.POINT_MARKET.pathname,
                icon: <SvgPointStore className="size-24" originalFill />,
              },
            ]),
      ],
    },

    INFO_GUIDE: {
      label: '이용 안내',
      items: [
        {
          label: '공지사항',
          href: DONATION_ROUTES.MY_ETC_NOTICE.pathname,
        },
        ...(isKakaobank
          ? []
          : [
              {
                label: '이벤트',
                href: DONATION_ROUTES.MY_ETC_EVENT.pathname,
              },
              {
                label: '문의 내역',
                href: DONATION_ROUTES.MY_INQUIRY_HISTORY.pathname,
              },
            ]),
        ...(isTossOrKbPay
          ? [
              {
                label: '마케팅 수신 동의',
                href: ROUTES.MY_NOTIFICATION_SETTINGS.pathname,
              },
            ]
          : []),
        {
          label: '이용약관',
          href: ROUTES.TERMS.pathname({ terms: TERM_CATEGORY.SERVICE_TERMS }),
        },
        {
          label: '개인정보 처리방침',
          href: ROUTES.TERMS.pathname({ terms: TERM_CATEGORY.PRIVACY_POLICY }),
        },
      ],
    },
  };

  const DEFAULT_NAVIGATION_GROUP_LIST: NavigationGroup[] = [
    NAVIGATION_GROUP_LIST.MY_MENU,
    NAVIGATION_GROUP_LIST.INFO_GUIDE,
  ];

  return {
    navigationGroupList: DEFAULT_NAVIGATION_GROUP_LIST,
  };
};
